import { render, statusBar, cardDisplay } from './components';
import { playDeckCompleteEffect, playMultiCardAcquireEffect, injectCardEffectStyles } from './CardEffects';
import { GameManager, CardPool } from '../game';
import { PlayerState, CardInstance, TierColors } from '../types';
import { getCardById } from '../cards';

export interface StarterDeckCallbacks {
  onSelect: (deck: CardInstance[]) => void;
  onSkip: () => void;
}

interface StarterDeckChoice {
  name: string;
  description: string;
  deck: CardInstance[];
}

export function renderStarterDeckSelection(
  game: GameManager,
  player: PlayerState,
  cardPool: CardPool,
  callbacks: StarterDeckCallbacks
): void {
  const choices = cardPool.generateStarterDeckChoices();

  render(`
    <div class="starter-selection" style="
      padding: 16px;
      max-width: 900px;
      margin: 0 auto;
      min-height: 100vh;
      min-height: 100dvh;
      background: linear-gradient(180deg, #1a1a2e 0%, #16213e 100%);
    ">
      <div style="text-align: center; margin-bottom: 20px;">
        <h1 style="
          color: #ffd700;
          font-size: clamp(1.2rem, 5vw, 1.8rem);
          margin: 0 0 8px 0;
          text-shadow: 0 0 20px rgba(255, 215, 0, 0.5);
        ">🃏 スターターデッキを選択</h1>
        <p style="color: #aaa; font-size: clamp(0.8rem, 3vw, 0.95rem); margin: 0;">
          3つのテーマから初期デッキを選んでください
        </p>
      </div>

      <div id="deckChoices" style="
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
        gap: 16px;
        justify-items: center;
      ">
        ${choices.map((choice, idx) => renderDeckChoice(choice, idx)).join('')}
      </div>

      <div style="text-align: center; margin-top: 24px; padding-bottom: 20px;">
        <button id="btnRandomDeck" style="
          background: transparent;
          border: 1px solid #666;
          color: #888;
          padding: 12px 24px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.9rem;
          min-height: 44px;
        ">🎲 ランダムで決める</button>
      </div>
    </div>
  `);

  // デッキ選択イベント
  choices.forEach((choice, idx) => {
    const btn = document.getElementById(`selectDeck_${idx}`);
    btn?.addEventListener('click', () => {
      showSelectionAnimation(idx, () => {
        callbacks.onSelect(choice.deck);
      });
    });
  });

  // ランダム選択
  document.getElementById('btnRandomDeck')?.addEventListener('click', () => {
    const randomIdx = Math.floor(Math.random() * choices.length);
    showSelectionAnimation(randomIdx, () => {
      callbacks.onSelect(choices[randomIdx].deck);
    });
  });
}

function renderDeckChoice(choice: StarterDeckChoice, idx: number): string {
  const themeColors = ['#3498db', '#e74c3c', '#9b59b6'];
  const themeIcons = ['⚖️', '⚔️', '🔮'];
  const color = themeColors[idx] ?? '#00fff5';
  const icon = themeIcons[idx] ?? '🃏';

  // デッキ内容のサマリー
  const categoryCount: Record<string, number> = {};
  let totalPower = 0;
  
  for (const card of choice.deck) {
    const def = getCardById(card.defId);
    if (def) {
      categoryCount[def.category] = (categoryCount[def.category] ?? 0) + 1;
      totalPower += card.currentPower;
    }
  }

  return `
    <div class="deck-choice" data-idx="${idx}" style="
      background: rgba(0,0,0,0.4);
      border: 2px solid ${color}40;
      border-radius: 16px;
      padding: 16px;
      width: 100%;
      max-width: 280px;
      min-width: 220px;
      cursor: pointer;
      transition: all 0.3s;
      position: relative;
      overflow: hidden;
    ">
      
      <!-- 背景グロー -->
      <div style="
        position: absolute;
        top: -50%;
        left: -50%;
        width: 200%;
        height: 200%;
        background: radial-gradient(circle, ${color}10 0%, transparent 70%);
        pointer-events: none;
      "></div>

      <!-- ヘッダー -->
      <div style="text-align: center; margin-bottom: 12px; position: relative;">
        <div style="font-size: 2rem; margin-bottom: 6px;">${icon}</div>
        <h3 style="color: ${color}; margin: 0; font-size: 1.1rem;">${choice.name}</h3>
        <p style="color: #888; font-size: 0.75rem; margin: 4px 0 0 0; line-height: 1.3;">${choice.description}</p>
      </div>

      <!-- カード一覧 -->
      <div style="
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
        justify-content: center;
        margin-bottom: 12px;
      ">
        ${choice.deck.map((card) => {
          const def = getCardById(card.defId);
          if (!def) return '';
          return `
            <div style="
              background: ${getCategoryColor(def.category)}20;
              border: 1px solid ${getCategoryColor(def.category)};
              border-radius: 6px;
              padding: 3px 5px;
              font-size: 0.65rem;
              color: #fff;
              display: flex;
              align-items: center;
              gap: 2px;
            ">
              <span>${getCategoryIcon(def.category)}</span>
              <span style="color: #ffd700;">${card.currentPower}</span>
            </div>
          `;
        }).join('')}
      </div>

      <!-- スタッツ -->
      <div style="
        background: rgba(0,0,0,0.3);
        border-radius: 8px;
        padding: 8px;
        margin-bottom: 12px;
        font-size: 0.8rem;
      ">
        <div style="display: flex; justify-content: space-between; color: #aaa;">
          <span>枚数</span>
          <span style="color: #fff;">${choice.deck.length}枚</span>
        </div>
        <div style="display: flex; justify-content: space-between; color: #aaa; margin-top: 3px;">
          <span>合計パワー</span>
          <span style="color: #ffd700;">⚡${totalPower}</span>
        </div>
        <div style="display: flex; justify-content: space-between; color: #aaa; margin-top: 3px;">
          <span>平均パワー</span>
          <span style="color: #ffd700;">⚡${(totalPower / choice.deck.length).toFixed(1)}</span>
        </div>
      </div>

      <!-- カテゴリー分布 -->
      <div style="display: flex; justify-content: center; gap: 6px; margin-bottom: 12px; font-size: 0.7rem;">
        ${Object.entries(categoryCount).map(([cat, count]) => `
          <span style="color: #aaa;">
            ${getCategoryIcon(cat)}${count}
          </span>
        `).join('')}
      </div>

      <!-- 選択ボタン -->
      <button id="selectDeck_${idx}" style="
        width: 100%;
        background: linear-gradient(135deg, ${color} 0%, ${color}80 100%);
        border: none;
        color: #fff;
        padding: 10px;
        border-radius: 8px;
        font-size: 0.9rem;
        font-weight: bold;
        cursor: pointer;
        min-height: 44px;
      ">選択する</button>
    </div>
  `;
}

async function showSelectionAnimation(selectedIdx: number, onComplete: () => void): Promise<void> {
  injectCardEffectStyles();
  
  const choices = document.querySelectorAll('.deck-choice');
  
  choices.forEach((el, idx) => {
    const htmlEl = el as HTMLElement;
    if (idx === selectedIdx) {
      htmlEl.style.transform = 'scale(1.05)';
      htmlEl.style.boxShadow = '0 0 40px rgba(255, 215, 0, 0.6)';
      htmlEl.style.borderColor = '#ffd700';
    } else {
      htmlEl.style.opacity = '0.3';
      htmlEl.style.transform = 'scale(0.95)';
    }
  });

  // 選択演出
  await new Promise((resolve) => setTimeout(resolve, 500));
  
  // デッキシャッフル演出
  const selectedChoice = choices[selectedIdx] as HTMLElement;
  if (selectedChoice) {
    // オーバーレイ表示
    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position: fixed;
      top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(0,0,0,0.8);
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      z-index: 1000;
      opacity: 0;
      transition: opacity 0.3s;
    `;
    
    overlay.innerHTML = `
      <div style="
        color: #ffd700;
        font-size: 1.5rem;
        font-weight: bold;
        margin-bottom: 20px;
        text-shadow: 0 0 20px #ffd700;
      ">デッキ準備中...</div>
      <div id="shuffleContainer" style="
        position: relative;
        width: 100px;
        height: 140px;
      "></div>
    `;
    
    document.body.appendChild(overlay);
    await new Promise((resolve) => setTimeout(resolve, 50));
    overlay.style.opacity = '1';
    
    // シャッフル演出
    const shuffleContainer = document.getElementById('shuffleContainer');
    if (shuffleContainer) {
      await playDeckCompleteEffect(shuffleContainer, 6);
    }
    
    // フェードアウト
    overlay.style.opacity = '0';
    await new Promise((resolve) => setTimeout(resolve, 300));
    overlay.remove();
  }

  onComplete();
}

function getCategoryIcon(category: string): string {
  const icons: Record<string, string> = {
    warrior: '⚔️',
    thief: '🗡️',
    mage: '🔮',
    machine: '⚙️',
    undead: '💀',
    dragon: '🐉',
    spirit: '✨',
    beast: '🐺',
    angel: '👼',
    demon: '😈',
    golem: '🗿',
    castle: '🏰',
    city: '🏙️',
    space: '🚀',
    movie: '🎬',
    ship: '🚢',
    ghost: '👻',
    park: '🎡',
  };
  return icons[category] ?? '?';
}

function getCategoryColor(category: string): string {
  const colors: Record<string, string> = {
    warrior: '#e74c3c',
    thief: '#9b59b6',
    mage: '#3498db',
    machine: '#95a5a6',
    undead: '#1abc9c',
    dragon: '#e67e22',
    spirit: '#00bcd4',
    beast: '#8d6e63',
    angel: '#ffd700',
    demon: '#8e44ad',
    golem: '#607d8b',
    castle: '#8b4513',
    city: '#4682b4',
    space: '#483d8b',
    movie: '#dc143c',
    ship: '#20b2aa',
    ghost: '#4b0082',
    park: '#ff69b4',
  };
  return colors[category] ?? '#666';
}
